/**
 * The mark, as geometry, in its own 24x20 box.
 *
 * `tools/brand/render-crown.py` holds the same seven points — it has to, since it runs
 * under Python with no way to import this. If one changes, change both; they are checked
 * against each other by tools/checks/crown-mark.mts.
 */
export const CROWN_POINTS: ReadonlyArray<readonly [number, number]> = [
  [3, 17],
  [3, 6],
  [8, 10.5],
  [12, 3],
  [16, 10.5],
  [21, 6],
  [21, 17],
];
export const CROWN_VIEWBOX = { w: 24, h: 20 } as const;
export const CROWN_GOLD = "#f5c518";

/**
 * Draw the crown onto a canvas.
 *
 * The share images are built with `canvas`, which cannot take a React component and
 * cannot wait on an image load without restructuring the download into something async.
 * So they get the same polygon rather than a lookalike glyph: `♛` is a chess queen in
 * most fonts, renders differently on every OS, and was never our mark.
 *
 * Returns the width drawn, so a caller can lay text out after it.
 */
export function drawCrown(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  height: number,
  color: string = CROWN_GOLD,
): number {
  const scale = height / CROWN_VIEWBOX.h;
  ctx.save();
  ctx.fillStyle = color;
  ctx.beginPath();
  CROWN_POINTS.forEach(([px, py], i) => {
    const cx = x + px * scale;
    const cy = y + py * scale;
    if (i === 0) ctx.moveTo(cx, cy);
    else ctx.lineTo(cx, cy);
  });
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  return CROWN_VIEWBOX.w * scale;
}

/**
 * The crown, wherever it appears.
 *
 * There were TWELVE crowns on this site and none of them was the logo: seven `♛` glyphs
 * (whose shape is whatever font the device happens to have — a chess queen on most, and
 * nothing like our mark) and five hand-copied SVG paths, one of which was still the old
 * outline-with-drops logo. A brand mark that renders as five different shapes is not a
 * brand mark.
 *
 * This is the same PNG the logo uses, so there is exactly one crown and changing it is
 * one file.
 *
 * `tone` keeps the one place the shape carries STATE rather than identity — the fap
 * button, which was a white outline before it was pressed and gold after. Same shape
 * either way now; only the treatment differs, which is what a state should change.
 */
export function CrownMark({
  size = 14,
  tone = "gold",
  className,
  title,
}: {
  size?: number;
  /** `muted` is the not-yet-fapped state: the same crown, drained to a pale outline-ish white.
   *  `white` is the crown ON a gold ground — the Gold tier badge (see Gold.tsx), where the
   *  fill and the mark have to trade places for the shape to read at all. `ink` is the
   *  black silhouette, for a Faps amount printed in black on a gold button. */
  tone?: "gold" | "muted" | "white" | "ink";
  className?: string;
  title?: string;
}) {
  return (
    <img
      src="/brand/crown-128.png"
      width={size}
      // The artwork is 24x20. Derived so a caller passing only a width never squashes it.
      height={Math.round((size * 20) / 24)}
      alt={title ?? ""}
      aria-hidden={title ? undefined : true}
      draggable={false}
      className={className}
      style={{
        display: "inline-block",
        verticalAlign: "-0.1em",
        // Recolouring the one gold PNG with a filter beats shipping a second and a third:
        // one asset, and the states cannot drift apart in shape.
        //
        // `brightness(0) invert(1)` is the two-step to pure white: brightness drives every
        // channel to zero first, so what inverts is a clean silhouette rather than the
        // artwork's own gold. Inverting the gold directly would give the crown's
        // complement — a pale blue — which is a different mark, not a white one.
        ...(tone === "muted"
          ? { filter: "grayscale(1) brightness(2.2)", opacity: 0.85 }
          : tone === "white"
            ? { filter: "brightness(0) invert(1)" }
            : tone === "ink"
              ? { filter: "brightness(0)" }
              : {}),
      }}
    />
  );
}
